import {Injectable} from '@angular/core';
import {Globals} from "@app/modulo-global/globals";
import {NotificationService} from "@app/core/services";
import * as moment from "smartadmin-plugins/bower_components/moment/moment";

export interface NotificationCenterItem {
    id: string;
    kind: string;
    priority: string;
    title: string;
    description: string;
    icon: string;
    badge: string;
    meta: string;
    timeLabel: string;
    reference: string;
    actionLabel: string;
    actionable: boolean;
    snoozeable: boolean;
    overdue: boolean;
    date: any;
    sortDate: number;
    raw: any;
    routeType: string;
    routeId: any;
}

export interface NotificationCenterGroup {
    id: string;
    title: string;
    description: string;
    items: NotificationCenterItem[];
}

export interface NotificationCenterResult {
    notifications: NotificationCenterItem[];
    taskCount: number;
    messageCount: number;
    noticeCount: number;
    urgentCount: number;
}

interface NotificationCenterState {
    read: {[id: string]: string};
    snoozed: {[id: string]: string};
}

const STATE_KEY_PREFIX = 'axia_notification_center_state_v1_';
const STATE_TTL_DAYS = 30;
const STATE_TTL_MS = STATE_TTL_DAYS * 24 * 3600 * 1000;
const REMINDER_KEY = 'notif_task';
const TIMER_KEY = 'temporizador_task_id';
const MASTER_TICK_MS = 55000;
const REMINDER_THROTTLE_MIN = 10;

@Injectable()
export class NotificationCenterService {
    private currentReminderRecords: any[] = [];
    private trackedTasks: Map<any, any> = new Map();
    private masterTimerHandle: any = null;

    constructor(
        private global: Globals,
        private notificationService: NotificationService
    ) {
    }

    public buildNotifications(tareasAsignadas: any[], chatsTemaNoLeidos: any[], chatsTareaNoLeidos: any[], proximosCumpleanos: any[]): NotificationCenterResult {
        let notifications: NotificationCenterItem[] = [];
        this.currentReminderRecords = [];
        this.pruneLocalState();

        tareasAsignadas = tareasAsignadas || [];
        chatsTemaNoLeidos = chatsTemaNoLeidos || [];
        chatsTareaNoLeidos = chatsTareaNoLeidos || [];
        proximosCumpleanos = proximosCumpleanos || [];

        for (let i = 0; i < tareasAsignadas.length; i++) {
            tareasAsignadas[i].type = 'tarea';
            notifications.push(this.buildTaskNotification(tareasAsignadas[i]));
        }

        localStorage.setItem(REMINDER_KEY, JSON.stringify(this.currentReminderRecords));

        for (let i = 0; i < chatsTemaNoLeidos.length; i++) {
            notifications.push(this.buildChatNotification(chatsTemaNoLeidos[i], 'tema'));
        }

        for (let i = 0; i < chatsTareaNoLeidos.length; i++) {
            notifications.push(this.buildChatNotification(chatsTareaNoLeidos[i], 'tarea'));
        }

        for (let i = 0; i < proximosCumpleanos.length; i++) {
            notifications.push(this.buildBirthdayNotification(proximosCumpleanos[i]));
        }

        notifications = this.applyLocalState(notifications);
        notifications = this.sortNotifications(notifications);

        return {
            notifications: notifications,
            taskCount: notifications.filter(x => x.kind == 'tarea').length,
            messageCount: notifications.filter(x => x.kind == 'mensaje').length,
            noticeCount: notifications.filter(x => x.kind == 'aviso').length,
            urgentCount: notifications.filter(x => x.priority == 'urgent').length,
        };
    }

    public groupNotifications(notifications: NotificationCenterItem[], filter: string): NotificationCenterGroup[] {
        let visible = this.filterNotifications(notifications, filter);
        let groups = [
            {id: 'urgent', title: 'Urgente', description: 'Requiere atención inmediata'},
            {id: 'today', title: 'Hoy', description: 'Pendiente o reciente'},
            {id: 'next', title: 'Próximos', description: 'Programado para más adelante'},
            {id: 'info', title: 'Informativo', description: 'Avisos generales'},
        ];

        let result: NotificationCenterGroup[] = [];
        for (let i = 0; i < groups.length; i++) {
            let items = visible.filter(x => x.priority == groups[i].id);
            if (items.length) {
                result.push({
                    id: groups[i].id,
                    title: groups[i].title,
                    description: groups[i].description,
                    items: items
                });
            }
        }
        return result;
    }

    public filterNotifications(notifications: NotificationCenterItem[], filter: string): NotificationCenterItem[] {
        if (filter == 'all') {
            return notifications;
        }
        return notifications.filter(x => x.kind == filter);
    }

    public getFilterCount(notifications: NotificationCenterItem[], filter: string) {
        return this.filterNotifications(notifications, filter).length;
    }

    public markAsRead(id: string) {
        let state = this.getState();
        state.read[id] = (new Date()).toISOString();
        delete state.snoozed[id];
        this.saveState(state);
        this.stopTrackedTask(id);
    }

    public markManyAsRead(items: NotificationCenterItem[]) {
        let state = this.getState();
        for (let i = 0; i < items.length; i++) {
            state.read[items[i].id] = (new Date()).toISOString();
            delete state.snoozed[items[i].id];
            this.stopTrackedTask(items[i].id);
        }
        this.saveState(state);
    }

    public restoreItems(items: NotificationCenterItem[]) {
        let state = this.getState();
        for (let i = 0; i < items.length; i++) {
            delete state.read[items[i].id];
            delete state.snoozed[items[i].id];
            this.restoreTrackedTask(items[i]);
        }
        this.saveState(state);
    }

    public snooze(id: string, option: string) {
        let until = new Date();
        if (option == '15m') {
            until.setMinutes(until.getMinutes() + 15);
        } else if (option == '1h') {
            until.setHours(until.getHours() + 1);
        } else {
            until.setDate(until.getDate() + 1);
            until.setHours(8, 0, 0, 0);
        }

        let state = this.getState();
        state.snoozed[id] = until.toISOString();
        delete state.read[id];
        this.saveState(state);
        this.stopTrackedTask(id);
    }

    public pruneLocalState() {
        let state = this.getState(false);
        this.pruneState(state);
    }

    public clearTimers() {
        if (this.masterTimerHandle) {
            clearInterval(this.masterTimerHandle);
            this.masterTimerHandle = null;
        }
        this.trackedTasks.clear();
    }

    private buildTaskNotification(item: any): NotificationCenterItem {
        let fechaHoy = new Date();
        let fechaProgramada = item.fecha_programada ? new Date(item.fecha_programada) : null;
        item.vencido = false;
        item.tsk = null;
        item.horas = null;

        if (fechaProgramada) {
            item.vencido = fechaHoy >= fechaProgramada;
            item.tsk = this.global.calculateDiffDates(new Date(), item.fecha_programada);
            if (item.tsk == "A las 00:00") {
                item.tsk = null;
                item.horas = null;
            } else {
                item.horas = this.global.calcularHorasRestantes(new Date(), item.fecha_programada);
            }

        }

        let dateKey = fechaProgramada ? fechaProgramada.toISOString() : 'sin-fecha';
        let notificationId = 'task-' + item.id_tarea + '-' + dateKey;

        if (fechaProgramada && !this.isLocallyMuted(notificationId)) {
            if (this.global.calcularHorasRestantes(new Date(), item.fecha_programada)) {
                this.registerTaskReminder(item.id_tarea);
                this.emitTaskReminder(item);
            }

            if (!item.vencido && item.horas && !this.trackedTasks.has(item.id_tarea)) {
                this.trackTaskForCountdown(item);
            }
        }

        return {
            id: notificationId,
            kind: 'tarea',
            priority: item.vencido ? 'urgent' : (fechaProgramada && this.isToday(fechaProgramada) ? 'today' : (fechaProgramada ? 'next' : 'info')),
            title: item.titulo_tarea || 'Tarea sin título',
            description: item.descripcion || 'Sin detalles sobre esta tarea',
            icon: item.vencido ? 'fa-warning' : 'fa-tasks',
            badge: item.vencido ? 'Vencida' : (item.horas ? item.horas : 'Asignada'),
            meta: item.usuario ? 'Por ' + item.usuario : (item.usuario_externo ? 'Con ' + item.usuario_externo : 'Tarea asignada'),
            timeLabel: item.tsk || (fechaProgramada ? 'Programada' : 'Sin fecha programada'),
            reference: 'T' + item.id_tarea + (item.actividad ? ' · A' + item.actividad : ''),
            actionLabel: 'Abrir tarea',
            actionable: true,
            snoozeable: true,
            overdue: item.vencido,
            date: fechaProgramada,
            sortDate: fechaProgramada ? fechaProgramada.getTime() : new Date(item.fecha_creacion || new Date()).getTime(),
            raw: item,
            routeType: 'tarea',
            routeId: item.id_tarea
        };
    }

    private buildChatNotification(item: any, tipo: string): NotificationCenterItem {
        item['type'] = 'msgs';
        item['message'] = tipo == 'tema'
            ? 'Mensaje' + (item.nro_mensajes > 1 ? 's' : '') + ' sin leer en el chat del tema "' + item.titulo + '"'
            : 'Mensaje' + (item.nro_mensajes > 1 ? 's' : '') + ' sin leer la tarea "' + item.titulo + '"';
        item['icon'] = item.nro_mensajes == 1 ? 'fa-comment-o' : 'fa-comments-o';
        item['tipo'] = tipo;
        item['time'] = this.global.calcularHorasRestantes(new Date(), item.ultimo_acceso);

        let routeType = tipo == 'tema' ? 'tema' : 'tarea';
        let routeId = tipo == 'tema' ? item.tema_id : item.tarea_id;
        let cantidad = item.nro_mensajes || 1;
        let fecha = item.fecha ? new Date(item.fecha) : (item.ultimo_acceso ? new Date(item.ultimo_acceso) : new Date());
        let dateKey = fecha ? fecha.getTime() : new Date().getTime();

        return {
            id: 'chat-' + routeType + '-' + routeId + '-' + cantidad + '-' + dateKey,
            kind: 'mensaje',
            priority: 'today',
            title: routeType == 'tema' ? 'Chat de tema' : 'Chat de tarea',
            description: item.message,
            icon: cantidad == 1 ? 'fa-comment-o' : 'fa-comments-o',
            badge: cantidad + ' sin leer',
            meta: routeType == 'tema' ? 'Tema vinculado' : 'Tarea vinculada',
            timeLabel: item.time || 'Sin leer',
            reference: routeType == 'tema' ? 'Tema' : 'Tarea',
            actionLabel: routeType == 'tema' ? 'Abrir tema' : 'Abrir tarea',
            actionable: true,
            snoozeable: false,
            overdue: false,
            date: fecha,
            sortDate: fecha.getTime(),
            raw: item,
            routeType: routeType,
            routeId: routeId
        };
    }

    private buildBirthdayNotification(item: any): NotificationCenterItem {
        let today = item.dias_para_cumpleanos == 0;
        let bday = new Date();
        bday.setDate(bday.getDate() + (item.dias_para_cumpleanos || 0));
        let stableKey = bday.toISOString().slice(0, 10);
        return {
            id: 'birthday-' + (item.nombre || '') + '-' + stableKey,
            kind: 'aviso',
            priority: today ? 'today' : 'info',
            title: today ? 'Cumpleaños hoy' : 'Cumpleaños próximo',
            description: today ? 'Hoy es el cumpleaños de ' + item.nombre : 'El cumpleaños de ' + item.nombre + (item.dias_para_cumpleanos > 1 ? ' es en ' + item.dias_para_cumpleanos + ' días' : ' es mañana'),
            icon: 'fa-birthday-cake',
            badge: today ? 'Hoy' : item.dias_para_cumpleanos + ' días',
            meta: 'Aviso interno', // la edad (edad_actual) es dato sensible: no se muestra aunque el backend la envíe
            timeLabel: today ? 'Hoy' : 'Próximo',
            reference: 'Cumpleaños',
            actionLabel: '',
            actionable: false,
            snoozeable: false,
            overdue: false,
            date: null,
            sortDate: new Date().getTime() + (item.dias_para_cumpleanos || 0) * 86400000,
            raw: item,
            routeType: '',
            routeId: null
        };
    }

    private applyLocalState(notifications: NotificationCenterItem[]): NotificationCenterItem[] {
        let state = this.getState();
        let now = new Date();
        let changed = false;
        let result: NotificationCenterItem[] = [];

        for (let i = 0; i < notifications.length; i++) {
            let item = notifications[i];
            if (state.read[item.id]) {
                continue;
            }
            if (state.snoozed[item.id]) {
                let until = new Date(state.snoozed[item.id]);
                if (until > now) {
                    continue;
                }
                delete state.snoozed[item.id];
                changed = true;
            }
            result.push(item);
        }

        if (changed) {
            this.saveState(state);
        }
        return result;
    }

    private isLocallyMuted(id: string) {
        let state = this.getState();
        let now = new Date();

        if (state.read[id]) {
            return true;
        }

        if (state.snoozed[id]) {
            let until = new Date(state.snoozed[id]);
            return until > now;
        }

        return false;
    }

    public sortNotifications(notifications: NotificationCenterItem[]): NotificationCenterItem[] {
        return notifications.sort((a, b) => {
            let ap = this.getPriorityOrder(a.priority);
            let bp = this.getPriorityOrder(b.priority);
            if (ap != bp) {
                return ap - bp;
            }
            return a.sortDate - b.sortDate;
        });
    }

    private getPriorityOrder(priority: string) {
        if (priority == 'urgent') {
            return 0;
        }
        if (priority == 'today') {
            return 1;
        }
        if (priority == 'next') {
            return 2;
        }
        return 3;
    }

    private isToday(date: Date) {
        let now = new Date();
        return date.getFullYear() == now.getFullYear() && date.getMonth() == now.getMonth() && date.getDate() == now.getDate();
    }

    private registerTaskReminder(id) {
        for (let i = 0; i < this.currentReminderRecords.length; i++) {
            if (this.currentReminderRecords[i].id == id) {
                return;
            }
        }

        let timestamp = null;
        let json = this.getLocalStorageArray(REMINDER_KEY);
        for (let i = 0; i < json.length; i++) {
            if (json[i].id == id) {
                timestamp = json[i].timestamp;
                break;
            }
        }
        this.currentReminderRecords.push({id: id, timestamp: timestamp});
    }

    private getReminderTimestamp(id) {
        for (let i = 0; i < this.currentReminderRecords.length; i++) {
            if (this.currentReminderRecords[i].id == id) {
                return this.currentReminderRecords[i].timestamp;
            }
        }
        return null;
    }

    private setReminderTimestamp(id, val) {
        for (let i = 0; i < this.currentReminderRecords.length; i++) {
            if (this.currentReminderRecords[i].id == id) {
                this.currentReminderRecords[i].timestamp = val;
                localStorage.setItem(REMINDER_KEY, JSON.stringify(this.currentReminderRecords));
                return;
            }
        }
        this.currentReminderRecords.push({id: id, timestamp: val});
        localStorage.setItem(REMINDER_KEY, JSON.stringify(this.currentReminderRecords));
    }

    private emitTaskReminder(item) {
        let ultimaEmision = this.getReminderTimestamp(item.id_tarea);
        if (!ultimaEmision) {
            this.setReminderTimestamp(item.id_tarea, (new Date()).toISOString());
        } else {
            let newDateObj = moment(ultimaEmision).add(REMINDER_THROTTLE_MIN, 'm').toDate();
            if (newDateObj > new Date()) {
                return;
            }
        }

        this.setReminderTimestamp(item.id_tarea, (new Date()).toISOString());

        let color = '#4170c4';
        let icono = 'fa fa-calendar-o shake animated';
        if (item.vencido) {
            color = '#cc9132';
            icono = 'fa fa-warning shake animated';
        }
        if (!item.descripcion) {
            item.descripcion = 'Sin detalles sobre esta tarea';
        }

        if (item.horas) {
            let h = this.global.calcularHorasRestantes(new Date(), item.fecha_programada, true).slice(0, 2);
            if (h != '00') {
                return;
            }

            this.notificationService.bigBox({
                title: item.titulo_tarea,
                content: item.descripcion,
                color: color,
                icon: icono,
                number: item.horas,
            });
        }
    }

    private trackTaskForCountdown(item) {
        let fp = new Date(item.fecha_programada);
        this.setTimedRecord(item.id_tarea, fp.toISOString());
        this.trackedTasks.set(item.id_tarea, item);
        this.ensureMasterTimer();
    }

    private ensureMasterTimer() {
        if (this.masterTimerHandle) {
            return;
        }
        this.masterTimerHandle = setInterval(() => this.runMasterTick(), MASTER_TICK_MS);
    }

    private runMasterTick() {
        if (this.trackedTasks.size === 0) {
            clearInterval(this.masterTimerHandle);
            this.masterTimerHandle = null;
            return;
        }
        let toRemove: any[] = [];
        this.trackedTasks.forEach((item, id) => {
            let t = this.global.calcularHorasRestantes(new Date(), item.fecha_programada, true);
            if (t == '00:00' || t == '05:00') {
                if (!item.descripcion) {
                    item.descripcion = 'Sin detalles sobre esta tarea';
                }
                let momento = (t == '05:00') ? 'En 5 minutos' : 'Ahora';
                this.notificationService.bigBox({
                    title: item.titulo_tarea,
                    content: item.descripcion,
                    color: '#198dc4',
                    icon: 'fa fa-clock-o fa-spin',
                    number: momento,
                });
                this.removeTimedRecord(id);
                toRemove.push(id);
            }
        });
        for (let i = 0; i < toRemove.length; i++) {
            this.trackedTasks.delete(toRemove[i]);
        }
        if (this.trackedTasks.size === 0) {
            clearInterval(this.masterTimerHandle);
            this.masterTimerHandle = null;
        }
    }

    private setTimedRecord(id, val) {
        let json = this.getLocalStorageArray(TIMER_KEY);
        for (let i = 0; i < json.length; i++) {
            if (json[i].id == id) {
                json[i].timestamp = val;
                localStorage.setItem(TIMER_KEY, JSON.stringify(json));
                return;
            }
        }
        json.push({id: id, timestamp: val});
        localStorage.setItem(TIMER_KEY, JSON.stringify(json));
    }

    private removeTimedRecord(id) {
        let json = this.getLocalStorageArray(TIMER_KEY);
        let res = json.filter(x => x.id != id);
        localStorage.setItem(TIMER_KEY, JSON.stringify(res));
    }

    private stopTrackedTask(notificationId: string) {
        if (!notificationId || notificationId.indexOf('task-') !== 0) {
            return;
        }

        let id = notificationId.split('-')[1];
        this.trackedTasks.delete(id);
        this.trackedTasks.delete(Number(id));
        this.removeTimedRecord(id);
        this.removeTimedRecord(Number(id));

        if (this.trackedTasks.size === 0 && this.masterTimerHandle) {
            clearInterval(this.masterTimerHandle);
            this.masterTimerHandle = null;
        }
    }

    private restoreTrackedTask(item: NotificationCenterItem) {
        if (!item || item.kind != 'tarea' || !item.raw || !item.raw.fecha_programada) {
            return;
        }

        let fechaProgramada = new Date(item.raw.fecha_programada);
        if (fechaProgramada <= new Date() || this.trackedTasks.has(item.raw.id_tarea)) {
            return;
        }

        item.raw.horas = this.global.calcularHorasRestantes(new Date(), item.raw.fecha_programada);
        if (item.raw.horas) {
            this.trackTaskForCountdown(item.raw);
        }
    }

    private getLocalStorageArray(key: string) {
        let json = JSON.parse(localStorage.getItem(key));
        return json || [];
    }

    private getStateKey(): string {
        let user = this.global.getUsuario();
        let suffix = (user && user.id) ? user.id : 'anon';
        return STATE_KEY_PREFIX + suffix;
    }

    private getState(prune: boolean = true): NotificationCenterState {
        let empty: NotificationCenterState = {read: {}, snoozed: {}};
        try {
            let state = JSON.parse(localStorage.getItem(this.getStateKey()));
            if (!state) {
                return empty;
            }
            state.read = state.read || {};
            state.snoozed = state.snoozed || {};
            if (prune) {
                this.pruneState(state);
            }
            return state;
        } catch (e) {
            return empty;
        }
    }

    private pruneState(state: NotificationCenterState) {
        let cutoff = Date.now() - STATE_TTL_MS;
        let pruned = false;

        Object.keys(state.read).forEach(k => {
            let ts = new Date(state.read[k]).getTime();
            if (!ts || ts < cutoff) {
                delete state.read[k];
                pruned = true;
            }
        });

        Object.keys(state.snoozed).forEach(k => {
            let ts = new Date(state.snoozed[k]).getTime();
            if (!ts || ts < cutoff) {
                delete state.snoozed[k];
                pruned = true;
            }
        });

        if (pruned) {
            this.saveState(state);
        }
    }

    private saveState(state: NotificationCenterState) {
        localStorage.setItem(this.getStateKey(), JSON.stringify(state));
    }
}
